import React, { useEffect, useContext } from 'react';
import { Grid, Table, PagingPanel, TableFilterRow, TableHeaderRow, TableColumnVisibility } from '@devexpress/dx-react-grid-bootstrap4';
import { FilteringState, PagingState, CustomPaging, SortingState, SelectionState } from '@devexpress/dx-react-grid';
import axios from 'axios';

import { FilterColumns, SortTable, ChangeTablePage, HandlePageLoading } from '../shared/TableEventHelpers';
import { OfficeFilterOperations } from '../shared/FilterOperations';
import NumberOfSearchResults from '../shared/NumberOfSearchResults';
import { InitialCommonCommands } from '../shared/PageStartup';
import TableHeaderCell from '../shared/TableHeaderCell';
import FilterResetter from '../shared/FilterResetter';
import ColumnChooser from '../shared/ColumnChooser';
import TableHeader from '../shared/TableHeader';
import TableFooter from '../shared/TableFooter';
import NoDataRow from '../shared/NoDataRow';
import TableRow from '../shared/TableRow';

import FilterCell from './FilterCell';
import TableCell from './TableCell';

import { DataContext } from '../../../stores/office-table-store';
import { DataContext as MainStoreContext } from '../../../store';
import Loading from '../../Loading';

import { useTranslation } from 'react-i18next';


const CancelToken = axios.CancelToken;
let cancel;

const OfficeTable = () => {
    const { store, dispatch } = useContext(DataContext);
    const mainStore = useContext(MainStoreContext);
    const { t } = useTranslation(['officeTable']);

    const {
        isLoading,
        tableSize,
        columnName,
        filters,
        requestUri,
        direction,
        pageSize,
        error,
        offices,
        columns,
        hiddenColumns,
        showCustomCols,
        currentPage,
        sortInfo,
        defaultFilters,
        tableColumnExtensions,
        defaultTableColumnExtensions,
    } = store;

    function translateColumnTitles() {
        columns.map((col) => {
            col.title = t(col.title);
        })
    }

    useEffect(() => {
        function fetchData() {
            cancel && cancel();

            const uri = `api/office/${columnName}/${direction}/${currentPage}/${pageSize}`;

            const strFilters = JSON.stringify(filters);

            const newRequestUri = uri + '/' + strFilters;

            if (newRequestUri === requestUri && mainStore.store.loadedTabs['/offices']) {
                return;
            }
            dispatch({ type: 'SET_ISLOADING_TRUE' });
            dispatch({ type: 'SET_REQUEST_URI', payload: newRequestUri });

            try {
                axios
                    .get(uri, {
                        params: {
                            filters: JSON.stringify(filters),
                        },
                        cancelToken: new CancelToken((c) => {
                            cancel = c;
                        }),
                        headers: {
                            Accept: 'application/json, text/plain, */*',
                            'Content-Type': 'application/json;charset=UTF-8',
                        },
                    })
                    .then((response) => {
                        const { rows, totalRows } = response.data;
                        dispatch({ type: 'FETCH_SUCCESS', offices: rows, totalRows });
                    })
                    .catch((thrown) => {
                        if (axios.isCancel(thrown)) {
                            //TODO Something when request is cancelled?
                        } else {
                            const error = thrown.response;
                            dispatch({ type: 'FETCH_ERROR', payload: error });
                        }
                    });
            } catch (error) {
                console.error(error);
                dispatch({ type: 'SET_ISLOADING_FALSE' });
            }
        }

        fetchData();
        translateColumnTitles();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [columnName, currentPage, filters, direction, pageSize]);

    useEffect(() => {
        const handleHiddenColumns = () => {
            const hidColumns = FilterColumns(columns, hiddenColumns, showCustomCols, defaultTableColumnExtensions, false, dispatch);
            if (hidColumns !== null) {
                dispatch({ type: 'HIDDEN_COLUMNS', payload: hidColumns });
            }
        };

        handleHiddenColumns();
        window.addEventListener('resize', handleHiddenColumns);

        return function cleanup() {
            window.removeEventListener('resize', handleHiddenColumns);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [columns, showCustomCols]);

    useEffect(() => {
        HandlePageLoading(isLoading);
    }, [isLoading]);

    useEffect(() => {
        InitialCommonCommands(t);
    }, []);

    const handleSortChange = (sorting) => SortTable(sorting, sortInfo, dispatch);

    const handlePageSizeChange = (pageSize) => ChangeTablePage(pageSize, dispatch, currentPage, tableSize);

    return (
        <Grid columns={columns} rows={offices} className='padding-right-60px'>
            <PagingState
                currentPage={currentPage - 1}
                pageSize={pageSize}
                onPageSizeChange={handlePageSizeChange}
                onCurrentPageChange={(currentPage) => {
                    dispatch({ type: 'CURRENT_PAGE', payload: currentPage + 1 });
                    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
                }}
            />
            <SortingState onSortingChange={handleSortChange} defaultSorting={[{ columnName, direction }]} />
            <CustomPaging totalCount={tableSize} />
            <SelectionState />

            <FilteringState defaultFilters={defaultFilters} />

            <Table
                columnExtensions={tableColumnExtensions}
                cellComponent={(props) => TableCell(props)}
                rowComponent={(props) => TableRow(props, 'office')}
                noDataRowComponent={(props) => NoDataRow(isLoading, error, props)}
            />

            <OfficeFilterOperations />

            <TableFilterRow showFilterSelector cellComponent={FilterCell} />
            <TableHeaderRow showSortingControls cellComponent={TableHeaderCell} />
            <PagingPanel pageSizes={[10, 25, 50, 10000]} />
            <TableColumnVisibility hiddenColumnNames={hiddenColumns} />
            <TableHeader tableSize={tableSize}>
                {/* <ConvertTableToExcel filters={filters} columns={columns} hiddenColumns={hiddenColumns} table="customer" /> */}
                <ColumnChooser
                    hiddenColumns={hiddenColumns}
                    columns={columns}
                    onHiddenColumnChange={(hiddenColumns) => {
                        dispatch({ type: 'HIDDEN_COLUMNS_AND_CUSTOM_COLS', hiddenColumns, showCustomCols: true });
                    }}
                    onReset={() => {
                        dispatch({ type: 'HIDDEN_COLUMNS_AND_CUSTOM_COLS', hiddenColumns: [], showCustomCols: false });
                    }}
                />
                <FilterResetter DataContext={DataContext} />
            </TableHeader>

            {isLoading ? <Loading /> : null}

            <TableFooter>
                <NumberOfSearchResults pageSize={pageSize} tableSize={tableSize} currentPage={currentPage} t={t} />
            </TableFooter>
        </Grid>
    );
};

export default OfficeTable;